/**
 * 手账“重画”按钮（TRV-06；2026-09-25 I 放行；POST /travel/plans/{plan_id}/journal/redraw，CSRF + Idempotency-Key）。
 * - 能力门：/meta 的 capabilities 里 travel.journal_redraw 是 available 才出现（按配置算；不许点了才碰 503）；不是 available 什么都不显示。
 * - 只有主人自己点才发（付费生图，绝不自动触发）。请求体三样照抄页面此刻读到的计划：expected_revision（这一版的 plan_revision，
 *   也就是 TravelPlan.current_revision）、journal_id、redraw_ticket。
 * - 已发送而结果未确认时保留原请求与键，并禁止第二次提交；同键、同参数、换身份或重挂页面都不是重发许可。
 *   连点只发一次：发送中按钮不可点，另有一道同步闸挡住同一刻的两次点击。
 * - 回应：
 *   - 200：把返回的计划写回页面（一般变成“画着呢”，按钮随之消失）；这一页还能重画（凭据对不上此刻状态，后端没发起新尝试）就说一句；
 *   - 409 VERSION_CONFLICT：重读计划，提示可以再点，不自动再点；
 *   - 409 IDEMPOTENCY_IN_PROGRESS：原提交仍在处理，等待确认；不再次调用重画。
 *   - 409 IDEMPOTENCY_KEY_REUSED：前端的错（同一个键配了不同的请求体），保留原键并明确失败；
 *   - 404（这一页不能重画了 / 计划不在了）：说人话并重读；503 NOT_CONFIGURED：暂时不能重画。
 */
import { useCallback, useRef, useState, useSyncExternalStore } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TravelJournalRedrawRequest, TravelPlan } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { checkTravelResponse, travelScopeKey, useTravelScope, type CurrentTravel, type TravelScope } from "./currentScope";
import { Button } from "@/shared/ui";
import { isPlanNotFound, planQueryKey } from "./data";

/** 能力表里这一项的键（I 定）。 */
export const REDRAW_CAPABILITY = "travel.journal_redraw";

/** 返回的计划里，这一页还能不能重画（没画成 / 结果没确认，而且给了凭据）。 */
function stillRedrawable(plan: TravelPlan, journalId: string): boolean {
  const revision = plan.revisions.find((r) => r.plan_revision === plan.current_revision);
  const page = revision?.journals.find((j) => j.journal_id === journalId);
  return Boolean(page?.redraw_ticket?.trim()) && (page?.image_status === "failed" || page?.image_status === "unknown");
}

export function JournalRedraw({ planId, petId, expectedRevision, journalId, ticket, unknown }: {
  planId: string;
  petId: string | null;
  /** 这一版的 plan_revision（＝TravelPlan.current_revision，页面显示的就是这一版）。 */
  expectedRevision: number;
  journalId: string;
  ticket: string;
  /**
   * 图片结果 unknown 本身不是某次已发送重画的回执；仍可由主人明确发起正常重画。
   * 只有原真实提交未确认时才锁住操作，原 mutation 元数据保留，不从图片/时间推断完成。
   */
  unknown: boolean;
}) {
  const { platform, visits } = useServices();
  const authority = useTravelScope();
  const scope = authority.scope;
  const queryClient = useQueryClient();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => platform.meta(), staleTime: 60_000, enabled: env.dataMode === "live" });
  const available = meta.data?.capabilities.some((capability) => capability.key === REDRAW_CAPABILITY && capability.status === "available") ?? false;
  const planKey = planQueryKey(scope?.userId ?? null, petId, planId, scope);
  const keyRef = useRef<{ signature: string; key: string; scope: string; unresolved: boolean } | null>(null);
  const gate = useRef<string | null>(null);
  const [note, setNote] = useState<{ scope: string; text: string } | null>(null);
  const noteFor = (target: TravelScope, text: string | null) => setNote(text ? { scope: travelScopeKey(target), text } : null);
  type Request = { body: TravelJournalRedrawRequest; key: string; scope: TravelScope; keyOfPlan: ReturnType<typeof planQueryKey>; guard: ReturnType<typeof useTravelScope>; accepted?: boolean; sent?: boolean };

  // An actual uncertain request belongs to this pet/plan operation, even when
  // current member or household epoch changes. Never infer settlement from a
  // later request, matching key/body, image status or a plan GET.
  const matchingAttempts = () => queryClient.getMutationCache().findAll({
    mutationKey: ["travel", "journal-redraw"],
    predicate: m => m.options.mutationKey?.[3] === petId && m.options.mutationKey?.[4] === planId,
  });
  const hasUnconfirmed = () => matchingAttempts().some(m => {
    const original = m.state.variables as Request | undefined;
    return Boolean(original && !original.accepted);
  });
  const awaitingOriginal = useSyncExternalStore(
    useCallback((changed: () => void) => queryClient.getMutationCache().subscribe(changed), [queryClient]),
    hasUnconfirmed, () => false,
  );

  const redraw = useMutation({
    mutationKey: ["travel", "journal-redraw", scope?.userId, petId, planId],
    gcTime: Infinity, // Keep the original unknown operation across page remounts. No replay. 
    mutationFn: async (request: Request) => {
      const { guard, scope: target, body, key, keyOfPlan } = request;
      await guard.verify();
      const cached = queryClient.getQueryData<CurrentTravel<TravelPlan>>(keyOfPlan);
      const revision = cached?.value.revisions.find(r => r.plan_revision === cached.value.current_revision);
      const page = revision?.journals.find(j => j.journal_id === body.journal_id);
      if (!guard.current() || !cached?.scope || travelScopeKey(cached.scope) !== travelScopeKey(target)
          || cached.value.plan_id !== planId || cached.value.current_revision !== body.expected_revision
          || page?.redraw_ticket !== body.redraw_ticket || !stillRedrawable(cached.value, body.journal_id)) throw Error("这页手账的当前凭据尚未确认，请重新查看。");
      // Explicit first submission only. An unresolved original request is
      // blocked before mutate; its ID/body are never replay permission.
      request.sent = true;
      const plan = await visits.redrawTravelJournal(planId, target.petId, body, key);
      const after = await guard.verify();
      checkTravelResponse(plan, target, planId);
      return { ...after, value: plan };
    },
    retry: false,
    onSuccess: (result, request) => {
      if (!request.guard.current()) return;
      queryClient.setQueryData(request.keyOfPlan, result);
      request.accepted = true;
      if (keyRef.current?.key === request.key) keyRef.current.unresolved = false;
      noteFor(request.scope, stillRedrawable(result.value, request.body.journal_id) ? "这次没有开始重画：这一页的状态刚变过，按现在的状态再点一次。" : null);
    },
    onError: async (error, request) => {
      const err = toApiError(error);
      // An original explicit rejection is not an ambiguous paid result. Keep
      // its ID but permit the owner's next click with freshly read new params.
      if (!request.sent || err.code === "VERSION_CONFLICT" || err.isCapabilityUnavailable || err.isNotFound) {
        request.accepted = true;
        if (keyRef.current?.key === request.key) keyRef.current.unresolved = false;
      }
      // Settling the original definite refusal is operation bookkeeping. It
      // must survive an expired projection epoch, but must not update its UI,
      // any new-scope plan, or global cache from that stale response.
      if (!request.guard.current()) return;
      try { await request.guard.verify(); } catch { return; }
      if (!request.guard.current()) return;
      if (err.code === "VERSION_CONFLICT") {
        void queryClient.invalidateQueries({ queryKey: request.keyOfPlan });
        noteFor(request.scope, "这份计划刚更新过，已经重新读了一遍；还想重画的话，再点一次。");
      } else if (err.code === "IDEMPOTENCY_KEY_REUSED") {
        // The original ID is retained; a reused-key conflict is not permission to invent another operation.
        noteFor(request.scope, "这次重画没有确认成功，请先重新查看这页手账。");
      } else if (err.code === "IDEMPOTENCY_IN_PROGRESS") {
        noteFor(request.scope, "上一次点击还在处理，稍后刷新看看。");
      } else if (err.isCapabilityUnavailable) {
        noteFor(request.scope, "暂时不能重画：画手账图的服务还没准备好。");
      } else if (isPlanNotFound(err)) {
        void queryClient.invalidateQueries({ queryKey: request.keyOfPlan });
        noteFor(request.scope, "这份计划还没写好，或者已经不在了。");
      } else if (err.isNotFound) {
        void queryClient.invalidateQueries({ queryKey: request.keyOfPlan });
        noteFor(request.scope, "这一页手账现在不能重画了，已经重新读了一遍。");
      } else if (err.kind === "network" || err.kind === "timeout") {
        noteFor(request.scope, "上一次提交结果还没确认，暂时不能再次重画。");
      } else if (request.sent && !request.accepted) {
        noteFor(request.scope, "上一次提交结果还没确认，暂时不能再次重画。");
      } else {
        noteFor(request.scope, `这次没有重画成：${err.playerMessage}`);
      }
    },
    onSettled: (_data, _error, request) => {
      if (gate.current === request.key) gate.current = null;
    },
  });

  if (!available || !scope || scope.petId !== petId || !authority.current()) return null;

  const onClick = () => {
    if (gate.current || redraw.isPending) return;
    // Check the real cache again on the click boundary, including a cache event
    // that has not rendered yet. No body/key creation and no mutate while HOLD.
    if (hasUnconfirmed()) {
      noteFor(scope, "上一次提交结果还没确认，暂时不能再次重画。");
      return;
    }
    const body: TravelJournalRedrawRequest = { expected_revision: expectedRevision, journal_id: journalId, redraw_ticket: ticket };
    const signature = JSON.stringify([planId, body]);
    const scopeKey = travelScopeKey(scope);
    if (!keyRef.current) {
      const previous = queryClient.getMutationCache().findAll({ mutationKey: ["travel", "journal-redraw", scope.userId, petId, planId] }).at(-1);
      const request = previous?.state.variables as Request | undefined;
      if (request) keyRef.current = { signature: JSON.stringify([planId, request.body]), key: request.key, scope: travelScopeKey(request.scope), unresolved: !request.accepted };
    }
    // A remounted button may have inspected the old unknown attempt before
    // its definite refusal arrived. Reconcile only that original key's latest
    // attempt on this explicit click; never infer settlement from any error.
    if (keyRef.current?.unresolved) {
      const attempts = queryClient.getMutationCache().findAll({ mutationKey: ["travel", "journal-redraw", scope.userId, petId, planId] });
      const original = attempts.filter(m => (m.state.variables as Request | undefined)?.key === keyRef.current?.key).at(-1)?.state.variables as Request | undefined;
      if (original?.accepted) keyRef.current.unresolved = false;
    }
    if (keyRef.current?.unresolved) {
      noteFor(scope, "上一次提交结果还没确认，暂时不能再次重画。");
      return;
    }
    if (!keyRef.current || keyRef.current.signature !== signature || keyRef.current.scope !== scopeKey) keyRef.current = { signature, scope: scopeKey, key: newIdempotencyKey("journal-redraw"), unresolved: true };
    gate.current = keyRef.current.key;
    keyRef.current.unresolved = true;
    setNote(null);
    redraw.mutate({ body, key: keyRef.current.key, scope, keyOfPlan: planKey, guard: authority });

  };

  return (
    <div className="ps-plan-journal__redraw" data-testid="journal-redraw">
      <Button variant="secondary" icon="refresh" loading={redraw.isPending} disabled={awaitingOriginal} onClick={onClick}>
        {awaitingOriginal ? "等待确认重画结果" : "重画这张手账"}
      </Button>
      {/* 不写「生成」（UI-GEN-WORDING-01）：原来是「主动重画会重新生成一张」 */}
      {unknown && !awaitingOriginal ? <small>这张图片的结果还没确认；主动重画会另画一张。</small> : null}
      {note?.scope === travelScopeKey(scope) ? <p role="status">{note.text}</p> : awaitingOriginal ? <p role="status">上一次提交结果还没确认，暂时不能再次重画。</p> : null}
    </div>
  );
}
